import { faAngleLeft } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import {
  ReactElement,
  ReactNode,
  cloneElement,
  useCallback,
  useEffect,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { animated, useSpring, useTransition } from 'react-spring';
import { useMeasure } from 'react-use';
import styled, { css } from 'styled-components';

import Button from 'atoms/buttons/Button';
import IconButton from 'atoms/buttons/IconButton';
import { Info } from 'atoms/icons/Info';
import { Vertical } from 'atoms/layout/flex';
import { BodyL } from 'atoms/typography';
import useFeatureFlags from 'hooks/useFeatureFlags';
import useInfiniteScroll from 'hooks/useInfiniteScroll';
import { useKeyboardSelection } from 'hooks/useKeyboardSelection';
import { useShortcut } from 'hooks/useShortcut';
import useToggle from 'hooks/useToggle';
import { glossary, navLabels } from 'lib/glossary';
import { tabletAndAbove } from 'style/mediaQuery';

const Wrapper = styled.div`
  position: relative;
  isolation: isolate;
  flex: 1;
  color: var(--c-neutral-1000);
  overflow: hidden;
  width: var(--bench-width);
`;

const PlayerSelection = styled(animated.section)<{
  $useNewPlayerDetail?: boolean;
}>`
  margin-left: ${({ $useNewPlayerDetail }) =>
    $useNewPlayerDetail ? 'auto' : '0px'};
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  will-change: width;
  width: 100%;
  padding-top: var(--intermediate-unit);
`;

type PlayerSuggestionProps = {
  selected: boolean;
  highlighted: boolean;
  isInfoPanelOpen?: boolean;
};

const PlayerSuggestion = styled.button.attrs({
  type: 'button',
})<PlayerSuggestionProps>`
  position: relative;
  background: var(--c-neutral-200);
  border-radius: var(--double-unit);
  overflow: hidden;
  border: 2px solid transparent;
  ${({ isInfoPanelOpen }) =>
    isInfoPanelOpen &&
    css`
      border-radius: var(--unit);
    `}
  ${({ highlighted }) =>
    highlighted &&
    css`
      --accent-color: var(--c-brand-600);
      border: 2px solid var(--accent-color, transparent);
    `}
  ${({ selected }) =>
    selected &&
    css`
      --accent-color: var(--c-brand-600);
      background: rgba(var(--c-rgb-brand-600), 0.5);
    `}
`;

const PlayerInfos = styled(animated.aside)<{ $useNewPlayerDetail?: boolean }>`
  color: var(--c-neutral-1000);
  position: fixed;
  left: ${({ $useNewPlayerDetail }) => ($useNewPlayerDetail ? 0 : 14 * 8)}px;
  top: 0;
  bottom: 0;
  overflow: auto;
  height: var(--100vh);

  width: ${({ $useNewPlayerDetail }) =>
    `calc(var(--bench-width) - ${$useNewPlayerDetail ? 8 * 10 : 14 * 8}px)`};
  border-top-right-radius: var(--double-unit);
  border-bottom-right-radius: var(--double-unit);
  ${({ $useNewPlayerDetail }) =>
    $useNewPlayerDetail &&
    css`
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
      border-right: 1px solid var(--c-neutral-300);
    `}
  display: flex;
  flex-direction: column;
`;

const InfosWrapper = styled.div`
  flex: 1;
`;

const InfoButton = styled.span`
  display: flex;
  border-radius: 50%;
  padding: var(--intermediate-unit);
  width: calc(5 * var(--unit));
  margin-left: auto;
  line-height: var(--double-unit);
  @media ${tabletAndAbove} {
    background-color: transparent;
    transition: background-color 0.25s ease-in;
    ${PlayerSuggestion}:hover & {
      background-color: rgba(255, 255, 255, 0.1);
    }
  }
`;

const StickyHeader = styled.header`
  overflow: hidden;
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1;
  padding: var(--double-unit);
`;

const Main = styled(Vertical)`
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
`;

const StickyFooter = styled.footer`
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  max-height: 50%;
  z-index: 1;
  background: linear-gradient(
    to bottom,
    rgba(var(--c-rgb-neutral-100), 0),
    rgba(var(--c-rgb-neutral-100), 0.25),
    rgba(var(--c-rgb-neutral-100), 0.9)
  );
`;

const Cards = styled(Vertical)<{ useNewPlayerDetail?: boolean }>`
  padding: 0
    ${({ useNewPlayerDetail }) =>
      useNewPlayerDetail ? 'var(--intermediate-unit)' : 'var(--double-unit)'};
`;

const StyledVertical = styled(Vertical)`
  &.fullHeight {
    flex: 1;
  }
`;

const CollapseButton = styled(IconButton).attrs({
  icon: faAngleLeft,
  color: 'tertiary',
})`
  position: absolute;
  top: var(--double-unit);
  right: var(--double-unit);
`;

const CTAWrapper = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  padding: var(--double-unit);
  background: linear-gradient(to top, rgba(0, 0, 0, 1) 65%, rgba(0, 0, 0, 0));
  & > * {
    flex: 1;
  }
`;

type Props = {
  header: ReactElement;
  footer?: ReactElement;
  cards: ReactElement[];
  children?: ReactNode;
  selectedIndex: number;
  cardsForSale?: ReactNode;
  empty?: ReactNode;
  selectCardAtIndex: (index: number | null) => void;
  infosForCardAtIndex: (index: number, onClose: () => void) => ReactNode;
  onOpenInfoPanel?: (index: number) => void;
  loadMore: () => void;
  hasMore: boolean;
  loading: boolean;
  ftue?: boolean;
};

export const Bench = ({
  header,
  footer,
  cards,
  children,
  cardsForSale,
  empty,
  loadMore,
  hasMore,
  loading,
  selectedIndex,
  selectCardAtIndex,
  infosForCardAtIndex,
  onOpenInfoPanel,
  ftue,
}: Props) => {
  const { formatMessage } = useIntl();
  const [isInfoPanelOpen, toggleInfoPanel] = useToggle(false);
  const {
    flags: { useNewPlayerDetail = false },
  } = useFeatureFlags();

  const {
    highlightedItemIndex: highlightedCardIndex,
    setHighlightedItemIndex: setHighlightedCardIndex,
  } = useKeyboardSelection({
    selectedIndex,
    selectItemAtIndex: selectCardAtIndex,
    nbItems: cards.length,
    redirectIfNotFound: true,
  });

  const onToggleInfoPanel = useCallback(() => {
    if (!isInfoPanelOpen) {
      onOpenInfoPanel?.(highlightedCardIndex);
    }

    toggleInfoPanel();
  }, [highlightedCardIndex, isInfoPanelOpen, onOpenInfoPanel, toggleInfoPanel]);

  const { InfiniteScrollLoader } = useInfiniteScroll(
    loadMore,
    hasMore,
    loading
  );

  useShortcut(
    'Backspace',
    () => {
      selectCardAtIndex(null);
    },
    [selectCardAtIndex]
  );
  useShortcut('?', onToggleInfoPanel, []);

  const [measurementDiv, { width: benchWidth }] = useMeasure<HTMLDivElement>();

  const playerSelectionWidth = useNewPlayerDetail ? 8 * 10 : 14 * 8;
  const playerSelectionStyles = useSpring({
    to: {
      width: isInfoPanelOpen ? playerSelectionWidth : benchWidth || undefined,
    },
  });

  const infoPanelTransition = useTransition(isInfoPanelOpen, {
    from: { x: '-100%', opacity: 0 },
    enter: { x: '0%', y: '0%', opacity: 1 },
    leave: { x: '-100%', opacity: 0 },
    reverse: isInfoPanelOpen,
  });

  useEffect(() => {
    if (cards.length === 0 && isInfoPanelOpen && !loading) {
      onToggleInfoPanel();
    }
  }, [cards.length, isInfoPanelOpen, loading, onToggleInfoPanel]);

  return (
    <Wrapper ref={measurementDiv}>
      {infoPanelTransition(
        (styles, isOpen) =>
          isOpen && (
            <PlayerInfos
              style={styles}
              $useNewPlayerDetail={useNewPlayerDetail}
            >
              {highlightedCardIndex !== null ? (
                <>
                  <InfosWrapper>
                    {infosForCardAtIndex(highlightedCardIndex, toggleInfoPanel)}
                  </InfosWrapper>
                  {!useNewPlayerDetail && (
                    <>
                      <CollapseButton
                        onClick={onToggleInfoPanel}
                        aria-label={formatMessage(glossary.close)}
                      />
                      <CTAWrapper>
                        {highlightedCardIndex === selectedIndex ? (
                          <Button
                            size="small"
                            color="red"
                            stroke
                            onClick={() => {
                              selectCardAtIndex(null);
                            }}
                          >
                            <FormattedMessage
                              id="Bench.info.remove_from_lineup"
                              defaultMessage="Remove from lineup"
                            />
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            color="primary"
                            onClick={() => {
                              selectCardAtIndex(highlightedCardIndex);
                            }}
                          >
                            <FormattedMessage
                              id="Bench.info.add_to_lineup"
                              defaultMessage="Add to lineup"
                            />
                          </Button>
                        )}
                      </CTAWrapper>
                    </>
                  )}
                </>
              ) : null}
            </PlayerInfos>
          )
      )}
      <PlayerSelection
        style={playerSelectionStyles}
        $useNewPlayerDetail={useNewPlayerDetail}
        className={classNames({ isInfoPanelOpen })}
      >
        {(!useNewPlayerDetail || !isInfoPanelOpen) && (
          <StickyHeader onClick={() => isInfoPanelOpen && onToggleInfoPanel()}>
            {cloneElement(header, {
              small: isInfoPanelOpen,
            })}
          </StickyHeader>
        )}
        {children}
        <Main gap={3}>
          <StyledVertical gap={2}>
            {(!useNewPlayerDetail || !isInfoPanelOpen) && !ftue && (
              <Cards useNewPlayerDetail={useNewPlayerDetail}>
                <BodyL as="p" bold>
                  <FormattedMessage {...navLabels.myCards} />
                </BodyL>
              </Cards>
            )}
            <Cards useNewPlayerDetail={useNewPlayerDetail}>
              {!loading && cards.length === 0 ? (
                empty
              ) : (
                <>
                  {cards.map((benchCard, index) => {
                    const onMoreInfos = (e: React.MouseEvent) => {
                      e.stopPropagation();
                      setHighlightedCardIndex(index);
                      onToggleInfoPanel();
                    };
                    return (
                      <PlayerSuggestion
                        isInfoPanelOpen={useNewPlayerDetail && isInfoPanelOpen}
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        selected={selectedIndex === index}
                        highlighted={highlightedCardIndex === index}
                        onClick={() => {
                          if (isInfoPanelOpen) {
                            if (index !== highlightedCardIndex) {
                              setHighlightedCardIndex(index);
                              onOpenInfoPanel?.(index);
                            } else {
                              onToggleInfoPanel();
                            }
                          } else {
                            selectCardAtIndex(index);
                          }
                        }}
                      >
                        {cloneElement(benchCard, {
                          small: useNewPlayerDetail && isInfoPanelOpen,
                          onMoreInfos,
                          children: isInfoPanelOpen ? undefined : (
                            <InfoButton
                              onClick={onMoreInfos}
                              title={formatMessage({
                                id: 'CardSuggestion.showInfos',
                                defaultMessage: 'show player info',
                              })}
                            >
                              <Info />
                            </InfoButton>
                          ),
                        })}
                      </PlayerSuggestion>
                    );
                  })}
                  <InfiniteScrollLoader />
                </>
              )}
            </Cards>
          </StyledVertical>
          {!isInfoPanelOpen && cardsForSale}
        </Main>
        {footer && (
          <StickyFooter>
            {cloneElement(footer, {
              small: isInfoPanelOpen,
            })}
          </StickyFooter>
        )}
      </PlayerSelection>
    </Wrapper>
  );
};
